// BUILD-042 (Oct 24) - the small presentational notices that go with loadState.ts. Presentation only; no authorization decision is made here.
import { needsPermissionMessage, TARGET_UNAVAILABLE_TEXT, type PermissionLabel } from './loadState';

/** The server refused a capability-gated list: say what is missing instead of pretending the list is empty. */
export function DeniedNotice({ thing, permission, testId }: { thing: string; permission: PermissionLabel; testId?: string }) {
  return (
    <div role="status" className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={testId ?? 'denied-notice'}>
      You don't have permission to view {thing}. It needs the "{permission}" permission. Ask an administrator who can change access.
    </div>
  );
}

/** The request itself failed (network / service). It never means "there is none". */
export function UnavailableNotice({ thing, message, onRetry, testId }: { thing: string; message?: string; onRetry?: () => void; testId?: string }) {
  const detail = message ? ` (${message.replace(/[.\s]+$/, '')})` : '';
  return (
    <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid={testId ?? 'unavailable-notice'}>
      {thing.charAt(0).toUpperCase() + thing.slice(1)} could not be loaded{detail}. <strong>This does not mean there is none.</strong>
      {onRetry && <>{' '}<button type="button" className="underline" onClick={onRetry}>Try again</button></>}
    </div>
  );
}

/** An employee-targeted read answered the intentionally non-enumerating 404. Neutral; never says the person lacks anything. */
export function TargetUnavailableNotice({ testId }: { testId?: string }) {
  return (
    <div role="status" className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2" data-testid={testId ?? 'target-unavailable-notice'}>
      {TARGET_UNAVAILABLE_TEXT}
    </div>
  );
}

/** Short inline reason shown beside / under a disabled action. */
export function PermissionHint({ label, what, testId }: { label: PermissionLabel; what?: string; testId?: string }) {
  return <span className="text-xs text-amber-800" data-testid={testId ?? 'permission-hint'}>{needsPermissionMessage(label, what)}</span>;
}

/** Renders the right notice for a non-ready, non-loading list view. Returns null for loading/ready so callers keep their own rendering for those. */
export function LoadViewNotice({ view, thing, permission, onRetry, testIdPrefix }: {
  view: import('./loadState').LoadView; thing: string; permission: PermissionLabel; onRetry?: () => void; testIdPrefix?: string;
}) {
  const p = testIdPrefix ?? thing.replace(/\s+/g, '-');
  if (view.kind === 'denied') return <DeniedNotice thing={thing} permission={permission} testId={`${p}-denied`} />;
  if (view.kind === 'targetUnavailable') return <TargetUnavailableNotice testId={`${p}-target-unavailable`} />;
  if (view.kind === 'unavailable') return <UnavailableNotice thing={thing} message={view.message} onRetry={onRetry} testId={`${p}-unavailable`} />;
  return null;
}
